import React, { useEffect, useState, useMemo } from 'react';
import clientAxios from '../../clients/clientAxios';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { notify } from 'react-notify-toast';
import LoadingScreen from '../../Layout/LoadingScreen';
import PaymentPlanEditPriceSchemaModal from './PaymentPlanEditPriceSchemaModal';
import moment from 'moment';
import PaymentPlanEditGeneral from './PaymentPlanEditGeneral';
import './paymentsPlan.css';
import { handleKeyDown } from '../../helpers/handleKeyDown';
import {
  handleInputValue,
  handleMaxInputValue,
} from '../../helpers/handleInputValue';
import InstallmentsDropdown from '../common/InstallmentsDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup,
  faTags,
  faStar,
  faPlus,
  faSearch,
  faTimes,
  faCopy,
  faTrashAlt,
  faCalendarAlt,
  faExclamationTriangle,
  faPercentage,
  faMoneyBillWave,
  faCheckCircle,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';

const PaymentsPlan = () => {
  const [planesData, setPlanesData] = useState([]);
  const [modalStatesOptionSee, setModalStatesOptionSee] = useState([]);
  const [modalStatesOptionEdit, setModalStatesOptionEdit] = useState([]);
  const [modalStatesOptionDelete, setModalStatesOptionDelete] = useState([]);
  const [show, setShow] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [checkedState, setCheckedState] = useState({});
  const [selectedOption, setSelectedOption] = useState('regular');
  const [searchTerm, setSearchTerm] = useState('');
  const [planUpdate, setPlanUpdate] = useState({
    name: '',
    description: '',
    totalAmount: 0,
    priceSchema: [],
    specialPlan: false,
    percentageOfDiscount: 0,
    fixInstallmentsOffer: null,
    promoName: '',
    promoPercentage: 0,
  });
  const FORMAT_DATE = 'DD/MM/YYYY';

  const { name, description, totalAmount, priceSchema } = planUpdate;

  const handleChangePlan = (event, fieldName, index) => {
    const newValue = event?.target ? event.target.value : event;
    setPlanUpdate((prevState) => {
      if (index >= 0) {
        const updatedPriceSchema = prevState.priceSchema.slice();
        updatedPriceSchema[index] = newValue;
        const calculatedTotalAmount = updatedPriceSchema.reduce(
          (accumulator, currentValue) => accumulator + +currentValue,
          0
        );
        return {
          ...prevState,
          priceSchema: updatedPriceSchema,
          totalAmount: calculatedTotalAmount,
        };
      } else {
        return {
          ...prevState,
          [fieldName]: newValue,
        };
      }
    });
  };

  const handleShowAndClose = (index, option) => {
    switch (option) {
      case 'see':
        const updatedModalStatesSee = [...modalStatesOptionSee];
        updatedModalStatesSee[index] = !updatedModalStatesSee[index];
        setModalStatesOptionSee(updatedModalStatesSee);
        break;
      case 'edit':
        const updatedModalStatesEdit = [...modalStatesOptionEdit];
        updatedModalStatesEdit[index] = !updatedModalStatesEdit[index];
        setModalStatesOptionEdit(updatedModalStatesEdit);
        break;
      case 'delete':
        const updatedModalStatesDelete = [...modalStatesOptionDelete];
        updatedModalStatesDelete[index] = !updatedModalStatesDelete[index];
        setModalStatesOptionDelete(updatedModalStatesDelete);
        break;
      default:
        setShow(!show);
        break;
    }
  };

  const getPlanesPayments = async () => {
    try {
      const planesResponse = await clientAxios.get('/planes');
      setPlanesData(planesResponse?.data || []);
      setIsLoading(false);
    } catch {
      notify.show('No se pudo traer los planes', 'error');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (planesData?.length > 0) {
      const initialCheckedState = planesData.reduce((accumulator, planItem) => {
        accumulator[planItem._id] = planItem.specialPlan;
        return accumulator;
      }, {});
      setCheckedState(initialCheckedState);
    }
  }, [planesData]);

  const normalizeNewPlan = (data) => {
    const amountQuotaFormated = data.priceSchema.map(Number);
    const totalPriceFormated = Number(data.totalAmount);
    const percentageOfDiscountFormated = Number(data.percentageOfDiscount);
    const fixInstallmentsOfferFormated = data.fixInstallmentsOffer
      ? Number(data.fixInstallmentsOffer)
      : null;
    return {
      ...planUpdate,
      priceSchema: amountQuotaFormated,
      totalAmount: totalPriceFormated,
      percentageOfDiscount: percentageOfDiscountFormated,
      fixInstallmentsOffer: fixInstallmentsOfferFormated,
    };
  };

  const createNewPlan = async () => {
    try {
      if (name && description && totalAmount && priceSchema.length !== 0) {
        const dataNormalized = normalizeNewPlan(planUpdate);
        const response = await clientAxios.post('/planes', dataNormalized);
        if (response.status === 201) {
          setShow(false);
          notify.show('Plan creado correctamente', 'success');
        }
        setPlanUpdate({
          name: '',
          description: '',
          totalAmount: 0,
          priceSchema: [],
          specialPlan: false,
          percentageOfDiscount: 0,
          fixInstallmentsOffer: null,
          promoName: '',
          promoPercentage: 0,
        });
        setFetchTrigger((prevState) => !prevState);
      } else {
        notify.show('Campo/s Vacio/s', 'error');
      }
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.message;

      if (status === 404) {
        notify.show(
          'El nombre que seleccionaste para el plan ya existe. Por favor utiliza un nombre distinto.',
          'error'
        );
      } else {
        setShow(false);
        notify.show(message || 'No se pudo crear el plan', 'error');
      }
    }
  };

  const handleDeletePlan = async (planId, index) => {
    try {
      const response = await clientAxios.delete(`/planes/${planId}`);
      if (response.status === 200) {
        handleShowAndClose(index, 'delete');
        notify.show('Plan eliminado correctamente', 'success');
        setFetchTrigger(true);
      }
    } catch {
      notify.show('No se pudo eliminar el plan', 'error');
    }
  };

  const handleSpecialPlan = async (planId, event) => {
    const isChecked = event.target.checked;

    setCheckedState((prevState) => ({
      ...prevState,
      [planId]: isChecked,
    }));

    setPlanUpdate((prevState) => ({
      ...prevState,
      specialPlan: isChecked,
    }));
    try {
      await clientAxios.patch(`/planes/${planId}`, { specialPlan: isChecked });
      setFetchTrigger((prevState) => !prevState);
      notify.show('Plan modificado correctamente', 'success');
    } catch {
      notify.show('No se pudo modificar el plan', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const addInput = () => {
    setPlanUpdate((prevState) => ({
      ...prevState,
      priceSchema: [...priceSchema, ''],
    }));
  };

  const deleteInput = (index) => {
    const newPriceSchema = [...priceSchema];
    newPriceSchema.splice(index, 1);
    const calculatedTotalAmount = newPriceSchema.reduce(
      (accumulator, currentValue) => accumulator + +currentValue,
      0
    );
    setPlanUpdate((prevState) => ({
      ...prevState,
      priceSchema: newPriceSchema,
      totalAmount: calculatedTotalAmount,
    }));
  };

  const handleFilterPlansChange = (category) => {
    setSelectedOption(category);
  };

  useEffect(() => {
    getPlanesPayments();
  }, []);

  useEffect(() => {
    if (fetchTrigger) {
      getPlanesPayments();
      setFetchTrigger(false);
    }
  }, [fetchTrigger]);

  const clonePlan = async (plan) => {
    try {
      const actualDate = moment().format('YYYY-MM-DD HH:mm:ss');
      const clonedPayload = {
        ...plan,
        name: `${plan.name} Copia`,
        createdAt: actualDate,
        updatedAt: actualDate,
      };
      await clientAxios.post('/planes', clonedPayload);
      notify.show('Plan clonado correctamente', 'success');
      setFetchTrigger(true);
    } catch (error) {
      notify.show('Ha ocurrido un error al clonar este plan.', 'error');
    }
  };

  const stats = useMemo(() => {
    const total = planesData?.length || 0;
    const regularCount = planesData?.filter((plan) => !plan.specialPlan).length || 0;
    const specialCount = planesData?.filter((plan) => plan.specialPlan).length || 0;
    const avgQuotas =
      total > 0
        ? Math.round(
            planesData.reduce(
              (accumulator, plan) =>
                accumulator + (plan.priceSchema?.length || 0),
              0
            ) / total
          )
        : 0;

    return {
      total,
      regularCount,
      specialCount,
      avgQuotas,
    };
  }, [planesData]);

  const filteredPlanesData = useMemo(() => {
    if (!planesData || planesData.length === 0) return [];

    return planesData.filter((plan) => {
      const matchesCategory =
        selectedOption === 'all'
          ? true
          : selectedOption === 'regular'
          ? !plan.specialPlan
          : plan.specialPlan;

      if (!matchesCategory) return false;

      if (!searchTerm.trim()) return true;

      const term = searchTerm.toLowerCase().trim();
      const planName = (plan.name || '').toLowerCase();
      const planDesc = (plan.description || '').toLowerCase();
      const planAmount = String(plan.totalAmount || '');

      return (
        planName.includes(term) ||
        planDesc.includes(term) ||
        planAmount.includes(term)
      );
    });
  }, [planesData, selectedOption, searchTerm]);

  return (
    <>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <div className='payments-plan-container'>
          {/* Header */}
          <div className='payments-plan-header'>
            <div className='payments-plan-title-box'>
              <h2>
                <span className='payments-plan-title-icon'>
                  <FontAwesomeIcon icon={faLayerGroup} />
                </span>
                Planes de Pago
              </h2>
              <p>Gestión de planes regulares, especiales y esquemas de cuotas</p>
            </div>

            <Button
              variant='primary'
              onClick={() => setShow(true)}
              className='btn-create-plan'
            >
              <FontAwesomeIcon icon={faPlus} />
              Nuevo Plan
            </Button>
          </div>

          {/* Stats Grid */}
          <div className='payments-plan-stats-grid'>
            <div className='payments-stat-card'>
              <div className='payments-stat-icon-wrapper blue'>
                <FontAwesomeIcon icon={faLayerGroup} />
              </div>
              <div className='payments-stat-info'>
                <span className='payments-stat-value'>{stats.total}</span>
                <span className='payments-stat-label'>Total de Planes</span>
              </div>
            </div>

            <div className='payments-stat-card'>
              <div className='payments-stat-icon-wrapper emerald'>
                <FontAwesomeIcon icon={faTags} />
              </div>
              <div className='payments-stat-info'>
                <span className='payments-stat-value'>{stats.regularCount}</span>
                <span className='payments-stat-label'>Planes Regulares</span>
              </div>
            </div>

            <div className='payments-stat-card'>
              <div className='payments-stat-icon-wrapper amber'>
                <FontAwesomeIcon icon={faStar} />
              </div>
              <div className='payments-stat-info'>
                <span className='payments-stat-value'>{stats.specialCount}</span>
                <span className='payments-stat-label'>Planes Especiales</span>
              </div>
            </div>

            <div className='payments-stat-card'>
              <div className='payments-stat-icon-wrapper purple'>
                <FontAwesomeIcon icon={faMoneyBillWave} />
              </div>
              <div className='payments-stat-info'>
                <span className='payments-stat-value'>{stats.avgQuotas}</span>
                <span className='payments-stat-label'>Promedio Cuotas</span>
              </div>
            </div>
          </div>

          {/* Toolbar (Tabs + Search) */}
          <div className='payments-plan-toolbar'>
            <div className='payments-filter-tabs'>
              <button
                type='button'
                className={`payments-tab-btn ${
                  selectedOption === 'regular' ? 'active' : ''
                }`}
                onClick={() => handleFilterPlansChange('regular')}
              >
                <FontAwesomeIcon icon={faTags} />
                Regulares
                <span className='payments-tab-badge'>{stats.regularCount}</span>
              </button>

              <button
                type='button'
                className={`payments-tab-btn ${
                  selectedOption === 'especial' ? 'active' : ''
                }`}
                onClick={() => handleFilterPlansChange('especial')}
              >
                <FontAwesomeIcon icon={faStar} />
                Especiales
                <span className='payments-tab-badge'>{stats.specialCount}</span>
              </button>

              <button
                type='button'
                className={`payments-tab-btn ${
                  selectedOption === 'all' ? 'active' : ''
                }`}
                onClick={() => handleFilterPlansChange('all')}
              >
                Todos
                <span className='payments-tab-badge'>{stats.total}</span>
              </button>
            </div>

            <div className='payments-search-wrapper'>
              <FontAwesomeIcon icon={faSearch} className='payments-search-icon' />
              <Form.Control
                type='text'
                placeholder='Buscar por nombre, descripción...'
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className='payments-search-input'
              />
              {searchTerm && (
                <button
                  type='button'
                  className='payments-search-clear'
                  onClick={() => setSearchTerm('')}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>

          {/* Table Display */}
          <div className='payments-table-card table-responsive'>
            {filteredPlanesData.length === 0 ? (
              <div className='payments-empty-state'>
                <FontAwesomeIcon
                  icon={faLayerGroup}
                  className='payments-empty-icon'
                />
                <h4 className='payments-empty-title'>
                  No se encontraron planes
                </h4>
                <p className='payments-empty-text'>
                  {searchTerm
                    ? `No hay planes que coincidan con "${searchTerm}".`
                    : 'No existen planes cargados en esta categoría actualmente.'}
                </p>
                {searchTerm ? (
                  <Button
                    variant='outline-primary'
                    size='sm'
                    onClick={() => setSearchTerm('')}
                  >
                    Limpiar búsqueda
                  </Button>
                ) : (
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={() => setShow(true)}
                  >
                    + Crear Nuevo Plan
                  </Button>
                )}
              </div>
            ) : (
              <Table hover className='payments-custom-table'>
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Descripción</th>
                    <th>Total & Promos</th>
                    <th>Creado el</th>
                    <th className='text-center'>Cuotas</th>
                    <th>Marcar Especial</th>
                    <th className='text-center'>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlanesData.map((plan, index) => (
                    <tr key={plan._id || index}>
                      <td>
                        <div className='plan-name-cell'>
                          <span className='plan-title-text'>{plan.name}</span>
                          <div className='plan-badge-wrapper'>
                            {plan.specialPlan ? (
                              <span className='plan-badge plan-badge-special'>
                                <FontAwesomeIcon icon={faStar} />
                                Especial
                              </span>
                            ) : (
                              <span className='plan-badge plan-badge-regular'>
                                Regular
                              </span>
                            )}
                            {plan.promoName && (
                              <span className='plan-badge plan-badge-promo'>
                                {plan.promoName}{' '}
                                {plan.promoPercentage ? `(${plan.promoPercentage}%)` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className='plan-description-text'>
                          {plan.description || '-'}
                        </div>
                      </td>

                      <td>
                        <div className='plan-amount-cell'>
                          <span className='plan-amount-value'>
                            ${Number(plan.totalAmount || 0).toLocaleString('es-AR')}
                          </span>
                          {plan.percentageOfDiscount > 0 && (
                            <span className='plan-discount-tag'>
                              <FontAwesomeIcon icon={faPercentage} />
                              {plan.percentageOfDiscount}% dto. contado
                            </span>
                          )}
                          {plan.fixInstallmentsOffer && (
                            <span className='plan-discount-tag text-info'>
                              {plan.fixInstallmentsOffer} cuotas sin interés
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className='plan-date-cell'>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                          {moment(plan.createdAt).format(FORMAT_DATE)}
                        </div>
                      </td>

                      <td className='text-center'>
                        <PaymentPlanEditPriceSchemaModal
                          key={plan._id || index}
                          planData={plan}
                          setFetchTrigger={setFetchTrigger}
                        />
                      </td>

                      <td>
                        <label className='special-plan-toggle'>
                          <input
                            type='checkbox'
                            onChange={(event) =>
                              handleSpecialPlan(plan._id, event)
                            }
                            checked={checkedState[plan._id] || false}
                          />
                          <span>Caso Especial</span>
                        </label>
                      </td>

                      <td>
                        <div className='plan-actions-group justify-content-center'>
                          <PaymentPlanEditGeneral
                            key={plan._id || index}
                            planData={plan}
                            setFetchTrigger={setFetchTrigger}
                          />

                          <button
                            type='button'
                            className='btn-action-custom btn-action-clone'
                            onClick={() => clonePlan(plan)}
                            title='Clonar Plan'
                          >
                            <FontAwesomeIcon icon={faCopy} />
                            Clonar
                          </button>

                          <button
                            type='button'
                            className='btn-action-custom btn-action-delete'
                            onClick={() => handleShowAndClose(index, 'delete')}
                            title='Eliminar Plan'
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                            Eliminar
                          </button>

                          {/* Delete Confirmation Modal */}
                          <Modal
                            show={modalStatesOptionDelete[index] || false}
                            onHide={() => handleShowAndClose(index, 'delete')}
                            centered
                            className='payments-modern-modal'
                          >
                            <Modal.Body>
                              <div className='delete-confirm-box'>
                                <FontAwesomeIcon
                                  icon={faExclamationTriangle}
                                  className='delete-warning-icon'
                                />
                                <h4 className='delete-confirm-title'>
                                  ¿Eliminar Plan?
                                </h4>
                                <p className='delete-confirm-text'>
                                  ¿Estás seguro de que deseas eliminar permanentemente el plan{' '}
                                  <span className='delete-plan-highlight'>
                                    "{plan.name}"
                                  </span>
                                  ? Esta acción no se puede deshacer.
                                </p>
                                <div className='delete-confirm-actions'>
                                  <Button
                                    variant='secondary'
                                    onClick={() =>
                                      handleShowAndClose(index, 'delete')
                                    }
                                  >
                                    Cancelar
                                  </Button>
                                  <Button
                                    variant='danger'
                                    onClick={() =>
                                      handleDeletePlan(plan._id, index)
                                    }
                                  >
                                    Sí, eliminar
                                  </Button>
                                </div>
                              </div>
                            </Modal.Body>
                          </Modal>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {/* Create New Plan Modal */}
          <Modal
            show={show}
            onHide={() => setShow(false)}
            size='lg'
            centered
            className='payments-modern-modal'
          >
            <Modal.Header closeButton>
              <Modal.Title>
                <FontAwesomeIcon icon={faPlus} />
                Nuevo Plan de Pago
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form>
                {/* General Info */}
                <div className='modal-section-title'>
                  <FontAwesomeIcon icon={faInfoCircle} />
                  Información Básica
                </div>
                <Form.Group className='mb-3'>
                  <Form.Label className='payments-form-label'>
                    Nombre del Plan
                  </Form.Label>
                  <Form.Control
                    name='name'
                    value={planUpdate.name}
                    onChange={(event) => handleChangePlan(event, 'name')}
                    type='text'
                    placeholder='Ej: Carrera Fullstack - Estándar'
                    className='payments-form-control'
                  />
                </Form.Group>
                <Form.Group className='mb-4'>
                  <Form.Label className='payments-form-label'>
                    Descripción
                  </Form.Label>
                  <Form.Control
                    type='text'
                    name='description'
                    value={planUpdate.description}
                    onChange={(event) =>
                      handleChangePlan(event, 'description')
                    }
                    placeholder='Breve descripción sobre el plan o destinatarios'
                    className='payments-form-control'
                  />
                </Form.Group>

                {/* Price Schema */}
                <div className='modal-section-title'>
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                  Esquema de Cuotas
                </div>
                <div className='modal-installments-box'>
                  {priceSchema.length === 0 ? (
                    <div className='text-muted small mb-3'>
                      No has agregado cuotas aún. Haz clic en "Añadir Cuota" para comenzar.
                    </div>
                  ) : (
                    priceSchema.map((valor, index) => (
                      <div key={index} className='modal-installment-row'>
                        <span className='modal-installment-badge'>
                          Cuota {index + 1}
                        </span>
                        <Form.Control
                          type='number'
                          name='priceSchema'
                          value={valor}
                          min={0}
                          onChange={(event) => {
                            const newValue = Number(event.target.value);
                            if (newValue < 0) {
                              handleChangePlan(
                                { target: { value: 0 } },
                                'priceSchema',
                                index
                              );
                            } else {
                              handleChangePlan(event, 'priceSchema', index);
                            }
                          }}
                          onKeyDown={handleKeyDown}
                          onInput={handleInputValue}
                          placeholder='Monto de la cuota'
                          className='payments-form-control flex-grow-1'
                        />
                        {index > 0 && (
                          <button
                            type='button'
                            className='btn-remove-installment'
                            onClick={() => deleteInput(index)}
                            title='Eliminar cuota'
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                    ))
                  )}

                  <button
                    type='button'
                    className='btn-add-installment'
                    onClick={addInput}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Añadir Cuota
                  </button>
                </div>

                {/* Pricing & Discounts */}
                <div className='modal-section-title'>
                  <FontAwesomeIcon icon={faPercentage} />
                  Precio Total & Promociones
                </div>
                <Form.Group className='mb-3'>
                  <Form.Label className='payments-form-label'>
                    Precio Total
                  </Form.Label>
                  <Form.Control
                    type='number'
                    name='totalAmount'
                    value={planUpdate.totalAmount}
                    onChange={(event) =>
                      handleChangePlan(event, 'totalAmount')
                    }
                    placeholder='Precio total del Plan'
                    className='payments-form-control'
                  />
                </Form.Group>

                <div className='row'>
                  <div className='col-md-6'>
                    <Form.Group className='mb-3'>
                      <Form.Label className='payments-form-label'>
                        Nombre de Promo
                      </Form.Label>
                      <Form.Control
                        type='text'
                        name='promoName'
                        value={planUpdate.promoName || ''}
                        onChange={(event) =>
                          handleChangePlan(event, 'promoName')
                        }
                        placeholder='Ej: HotSale, CyberMonday'
                        className='payments-form-control'
                      />
                    </Form.Group>
                  </div>
                  <div className='col-md-6'>
                    <Form.Group className='mb-3'>
                      <Form.Label className='payments-form-label'>
                        % Descuento de Promo
                      </Form.Label>
                      <Form.Control
                        type='number'
                        name='promoPercentage'
                        value={planUpdate.promoPercentage || ''}
                        onInput={handleMaxInputValue}
                        onKeyDown={handleKeyDown}
                        onChange={(event) =>
                          handleChangePlan(event, 'promoPercentage')
                        }
                        placeholder='Ej: 15%'
                        className='payments-form-control'
                      />
                    </Form.Group>
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-6'>
                    <Form.Group className='mb-3'>
                      <Form.Label className='payments-form-label'>
                        Descuento curso completo %
                      </Form.Label>
                      <Form.Control
                        type='number'
                        name='percentageOfDiscount'
                        value={planUpdate.percentageOfDiscount || ''}
                        onInput={handleMaxInputValue}
                        onKeyDown={handleKeyDown}
                        onChange={(event) =>
                          handleChangePlan(event, 'percentageOfDiscount')
                        }
                        placeholder='Ej: 10%'
                        className='payments-form-control'
                      />
                    </Form.Group>
                  </div>
                  <div className='col-md-6'>
                    <InstallmentsDropdown
                      value={planUpdate.fixInstallmentsOffer}
                      onChange={(event) =>
                        handleChangePlan(event, 'fixInstallmentsOffer')
                      }
                    />
                  </div>
                </div>
              </Form>
            </Modal.Body>
            <Modal.Footer className='border-0 pt-0'>
              <Button
                variant='secondary'
                onClick={() => setShow(false)}
              >
                Cancelar
              </Button>
              <Button
                variant='primary'
                onClick={createNewPlan}
                className='btn-create-plan'
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                Crear Plan
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </>
  );
};

export default PaymentsPlan;
